import { useState } from "react";
import { Button, SelectMode, DropDownInput, InputField } from "@/components"; // Ensure correct path
import { AiOutlineCheck } from "react-icons/ai";
import { useApi, useAlert, useConfirmationDialog } from "@/";
import { useDispatch } from "react-redux";
import { fetchCustomerCODDues } from "../../../app/Slices";

const CodRemittanceDue = ({
  setSheetOpen = false,
  data = [],
  amount = 0,
  uid,
}) => {
  const { callApi, loading } = useApi();
  const dispatch = useDispatch();
  const { alert, AlertDialog: Dialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();
  const [filters, setFilters] = useState({
    amountSelected: "",
    paymentMode: "",
    UTR: "",
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const paymentModeOption = [
    { value: "Bank Account", label: "Bank Account" },
    { value: "Wallet", label: "Wallet" },
  ];

  const getClosestShipments = (data, amount) => {
    // Filter data to only include shipments with status "Due"
    const dueShipments = data.filter((shipment) => shipment.status === "Due");

    // Sort filtered data from oldest to newest based on createdAt
    const sortedShipments = [...dueShipments].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    // If no due shipments, return empty result
    if (sortedShipments.length === 0) {
      return { matches: [] };
    }

    // Process shipments one by one in chronological order
    const findBestCombination = () => {
      let currentSum = 0;
      let currentShipments = [];

      // Add shipments one by one in chronological order
      for (const shipment of sortedShipments) {
        // Add this shipment
        currentShipments.push(shipment);
        currentSum += shipment.dueAmount;

        // If we've found an exact match, return it immediately
        if (Math.abs(currentSum - amount) <= 0.01) {
          return {
            matches: [
              {
                sum: currentSum,
                shipments: [...currentShipments],
              },
            ],
          };
        }

        // If we've exceeded the amount, we have two options:
        // 1. The current combination (which is above the target)
        // 2. The previous combination (which is below the target)
        if (currentSum > amount) {
          // If this is the first shipment and it's already above the target
          if (currentShipments.length === 1) {
            return {
              closestBelow: { sum: 0, shipments: [] },
              closestAbove: {
                sum: currentSum,
                shipments: [...currentShipments],
              },
            };
          }

          // Otherwise, we have both a below and above option
          const previousShipments = [...currentShipments];
          previousShipments.pop(); // Remove the last shipment

          const previousSum = currentSum - shipment.dueAmount;

          return {
            closestBelow: {
              sum: previousSum,
              shipments: previousShipments,
            },
            closestAbove: {
              sum: currentSum,
              shipments: [...currentShipments],
            },
          };
        }
      }

      // If we've gone through all shipments and still haven't exceeded the amount
      // Return what we have as the closest below
      return {
        closestBelow: {
          sum: currentSum,
          shipments: [...currentShipments],
        },
        closestAbove: { sum: Infinity, shipments: [] },
      };
    };

    return findBestCombination();
  };

  const generateAmountOptions = (result, amount) => {
    const amountOption = [];

    if (result.matches) {
      result.matches.forEach(({ sum, shipments }) => {
        const difference = sum - amount;
        // Round to nearest integer for suffix calculation
        const roundedDiff = Math.round(difference);

        const suffix =
          roundedDiff === 0
            ? "±0"
            : `${roundedDiff > 0 ? "+" : ""}${roundedDiff}`;

        amountOption.push({
          value: sum,
          label: sum.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          subOption: {
            label: `Shipment(${shipments.length}) ${suffix}`,
          },
        });
      });
    } else {
      const { closestBelow, closestAbove } = result;

      // Handle closest below with integer difference
      if (closestBelow && closestBelow.sum > 0) {
        const diff = Math.round(amount - closestBelow.sum);
        amountOption.push({
          value: closestBelow.sum,
          label: closestBelow.sum.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          subOption: {
            label: `Shipment(${closestBelow.shipments.length}) -${diff}`,
          },
        });
      }

      // Handle closest above with integer difference
      if (closestAbove && closestAbove.sum < Infinity) {
        const diff = Math.round(closestAbove.sum - amount);
        amountOption.push({
          value: closestAbove.sum,
          label: closestAbove.sum.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          subOption: {
            label: `Shipment(${closestAbove.shipments.length}) +${diff}`,
          },
        });
      }
    }

    return amountOption;
  };

  const closestResult = getClosestShipments(data, amount);

  console.log("ClosestResult::::::", closestResult);

  const amountOption = generateAmountOptions(closestResult, amount);
  console.log("AmountOption::::::", amountOption);

  const handleDueUpload = async () => {
    console.log("FormData:", filters);

    // Validation remains the same
    if (filters.amountSelected === "") {
      alert("Please select an amount");
      return;
    }
    if (filters.paymentMode === "") {
      alert("Please select a payment mode");
      return;
    }
    if (filters.paymentMode === "Bank Account" && filters.UTR === "") {
      alert("Please enter UTR/Ref No.");
      return;
    }
    await confirm(
      `Are you sure you want to upload COD due of ${filters.amountSelected}?`
    );
    try {
      // Find the selected shipment set based on chosen amount
      const selectedAmount = parseFloat(filters.amountSelected);
      let selectedShipments = [];

      // Check matches first
      if (closestResult.matches) {
        const match = closestResult.matches.find(
          (m) => m.sum === selectedAmount

        );
        console.log("Match::::::", match);
        if (match) selectedShipments = match.shipments;
      }

      // If no match found, check closest below/above
      if (!selectedShipments.length) {
        if (closestResult.closestBelow?.sum === selectedAmount) {
          selectedShipments = closestResult.closestBelow.shipments;
        } else if (closestResult.closestAbove?.sum === selectedAmount) {
          selectedShipments = closestResult.closestAbove.shipments;
        }
      }

      // Fallback if still not found (shouldn't happen with valid UI options)
      if (!selectedShipments.length) {
        alert("Invalid amount selection");
        return;
      }

      const response = await callApi(
        "/admin/customer/makeCustomerPayment",
        "POST",
        {
          shipmentIds: selectedShipments.map((shipment) => shipment.shipmentId),
          amount: parseFloat(filters.amountSelected),
          paymentMode: filters.paymentMode,
          utr: filters.UTR,
        },
        { userId: uid }
      );

      console.log("Due Upload Response:", response);

      if (!response.success) {
        alert(`Failed to upload COD due :- \n ${response.error}`);
        return;
      }
      await alert(`COD due uploaded successfully`);
      dispatch(fetchCustomerCODDues({ userId: uid }));
      setSheetOpen(false);
      console.log("Due Upload Response:", response);
    } catch (error) {
      console.error("Error uploading COD due:", error);
    }
  };

  return (
    <div className="flex flex-col gap-6 mt-4  pt-2">
      <SelectMode
        name="amountSelected"
        id="amountSelected"
        options={amountOption} //
        selectedValue={filters.amountSelected} //
        onChange={(val) => handleChange("amountSelected", val)}
        selectedClassName="bg-primary/10"
        className="w-full"
      />
      <DropDownInput
        label="Payment Mode"
        placeholder="Select Payment Mode"
        name="paymentMode"
        options={paymentModeOption}
        onChange={(val) => handleChange("paymentMode", val)}
        value={filters.paymentMode}
      />
      {filters.paymentMode === "Bank Account" && (
        <InputField
          label="UTR/Ref No."
          placeholder="Enter UTR/Ref No."
          name="UTR"
          type="text"
          value={filters.UTR}
          onChange={(val) => handleChange("UTR", val)}
        />
      )}
      {/* Footer Actions */}
      <div className="flex gap-2 justify-end items-center mt-4">
        <Button
          variant="default"
          size="sm"
          icon={AiOutlineCheck}
          onClick={handleDueUpload}
          isLoading={loading}
        >
          Upload Cod Due
        </Button>
      </div>
      <Dialog />
      <ConfirmationModal />
    </div>
  );
};

export default CodRemittanceDue;
